import { WebsocketData, WSService } from 'src/app/core/_services/ws.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { map, skip, takeUntil, switchMap, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ErrorCodes, GenericItemApiResponse, LoginResult, RegisterResult } from '../_interface/apiResponse';
import { CurrentUser } from '../_interface/user';
import { Router } from '@angular/router';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { IStorage, STORAGE_SERVICE } from '../_interface/storage';
import { NGXLogger } from 'ngx-logger';
import { CrossAppMessagingService } from './crossApp-messaging.service';
import { JwtHelperService } from './JwtHelperService';
import { logRetry, retryTransient } from '../_helpers/retry-transient';

export interface IRegisterData {
  firstName: string;
  lastName: string;
  eMail: string;
  password: string;
  /** Pun broj sa pozivnim kodom, npr. "+381 641234567" */
  phone: string;
  /** reCAPTCHA token (v3, akcija "register"); null kad provera nije konfigurisana. */
  recaptchaToken?: string | null;
}

/** Prvi novi pokušaj čitanja sačuvane prijave posle neuspeha; svaki sledeći duplo kasnije, najviše MAX. */
const TOKEN_RETRY_MS = 5000;
const TOKEN_RETRY_MAX_MS = 60000;

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  private _refreshTokenSubscription?: Subscription;

  /** Zakazan novi pokušaj čitanja sačuvane prijave (v. loadStoredToken). */
  private tokenRetryTimer?: number;
  private tokenRetryDelay = TOKEN_RETRY_MS;
  private tokenReadFailures = 0;
  private tokenReadInFlight = false;
  /** 'online' je stigao dok je čitanje (krenulo bez mreže) bilo u toku — ako padne, ponovi odmah. */
  private tokenRetryOnFail = false;

  tokenLoaded = new BehaviorSubject(false);
  socialUser: SocialUser | null = null;

  sessionExpired = new Subject<void>();

  currentUser = new BehaviorSubject<CurrentUser | null>(null);

  impersonateToken?: string;
  redirectUrl: string | null = null;

  private _token?: string | null | undefined;
  public get token(): string | null | undefined {
    return this._token;
  }
  public set token(value: string | null | undefined) {
    this._token = value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUser.value;
  }

  get currentUserID(): number {
    return this.currentUser.value?.userID || 0;
  }


  constructor(private http: HttpClient,
    private socialAuth: SocialAuthService,
    private jwt: JwtHelperService,
    private router: Router,
    private logger: NGXLogger,
    private ws: WSService,
    private crossAppMessaging: CrossAppMessagingService,
    @Inject(STORAGE_SERVICE) private storageService: IStorage) {

    this.currentUser.subscribe(
      user => {
        if (user) {
          crossAppMessaging.sendRawMessage({ action: 'setToken', data: this.token });
          crossAppMessaging.sendRawMessage({ action: 'setUser', data: user });
        }
      }
    )

    this.currentUser.pipe(
      filter(user => !!user),
      switchMap(user => timer(1000, 6 * 60 * 60 * 1000))
    ).subscribe({
      next: (user) => {
        this.refreshToken();
      }
    });

    this.socialAuth.authState.subscribe(
      (user: any) => this.socialUser = user
    )

    this.loadStoredToken();

    // Mreža se vratila — ne čekaj zakazani pokušaj čitanja sačuvane prijave.
    window.addEventListener('online', () => {
      if (this.tokenReadInFlight) {
        this.tokenRetryOnFail = true;
      } else if (this.tokenRetryTimer) {
        window.clearTimeout(this.tokenRetryTimer);
        this.tokenRetryTimer = undefined;
        this.scheduleStoredTokenRetry(true);
      }
    });

    // Prijava drugim putem (PIN, kod…) — sačuvanu više ne treba čitati.
    this.currentUser.pipe(
      filter(user => !!user)
    ).subscribe(() => this.cancelStoredTokenRetry());

    ws.observe("auth").subscribe(
      msg => {
        if (msg.data == "logout") {
          this.logger.info("WS reported logout - session expired");
          this.signOut().then(
            () => this.sessionExpired.next()
          );
        }
      }
    )

    ws.observe("sessionExpired").subscribe(
      msg => {
        if (this.currentUser.value?.sessionID == msg.data.sessionID) {
          this.logger.info("WS reported session expired");
          this.signOut().then(
            () => this.sessionExpired.next()
          );
        }
      }
    )

    ws.connected.subscribe(connected => {
      if (connected && this.tokenLoaded) {
        this.sendWSAuth();
      }
    });
  }

  /**
   * Čita sačuvanu prijavu. U browseru na *.emeni.rs ona stoji na app domenu (CrossDomainStorageService,
   * skriveni iframe) — bez mreže čitanje pukne, pa je uređaj ostajao na loginu i posle povratka veze,
   * sve do ručnog refresh-a (nemoguće na ekranu bez tastature). Zato se posle neuspeha pokušava ponovo
   * (odmah na 'online', inače sa rastućim razmakom) dok čitanje ne uspe ili se neko ne prijavi.
   */
  private loadStoredToken() {
    this.tokenRetryTimer = undefined;
    this.tokenReadInFlight = true;

    this.storageService.get('authToken').then(token => {
      this.tokenReadInFlight = false;
      if (!environment.production) {
        this.logger.log("AUTH retrieved from storage");
      }

      const failures = this.tokenReadFailures;
      this.cancelStoredTokenRetry();

      if (this.currentUser.value) {
        if (!environment.production) {
          this.logger.log("User already set");
        }
        this.markTokenLoaded();

        return;
      }

      this.token = token || '';

      this.setCurrentUser(token);
      this.markTokenLoaded();

      if (failures) {
        this.logger.info("AUTH: sačuvana prijava pročitana posle " + failures + " neuspelih pokušaja"
          + (this.currentUser.value ? " — korisnik vraćen" : ""));
      }
    },
      err => {
        this.tokenReadInFlight = false;
        if (!environment.production) {
          this.logger.log("get token timeout");
        }
        if (this.tokenReadFailures++ === 0) {
          this.logger.warn("AUTH: čitanje sačuvane prijave nije uspelo, pokušavam ponovo", err);
        }
        // login ekran se prikazuje odmah (KD guard čeka tokenLoaded); prijava se vraća kad čitanje uspe
        this.markTokenLoaded();
        const now = this.tokenRetryOnFail;
        this.tokenRetryOnFail = false;
        this.scheduleStoredTokenRetry(now);
      });
  }

  private scheduleStoredTokenRetry(immediately = false) {
    if (this.currentUser.value || this.tokenRetryTimer) {
      return;
    }
    if (immediately) {
      // veza se upravo vratila — odmah, a dalji razmaci ponovo od početka
      this.tokenRetryDelay = TOKEN_RETRY_MS;
      this.tokenRetryTimer = window.setTimeout(() => this.loadStoredToken(), 0);
      return;
    }
    this.tokenRetryTimer = window.setTimeout(() => this.loadStoredToken(), this.tokenRetryDelay);
    this.tokenRetryDelay = Math.min(this.tokenRetryDelay * 2, TOKEN_RETRY_MAX_MS);
  }

  private cancelStoredTokenRetry() {
    window.clearTimeout(this.tokenRetryTimer);
    this.tokenRetryTimer = undefined;
    this.tokenRetryDelay = TOKEN_RETRY_MS;
    this.tokenReadFailures = 0;
    this.tokenRetryOnFail = false;
  }

  /** tokenLoaded ide u true samo jednom — ponovni pokušaji ne smeju da ponovo okidaju pretplatnike. */
  private markTokenLoaded() {
    if (!this.tokenLoaded.value) {
      this.tokenLoaded.next(true);
    }
  }

  get isImperosnating() {
    return (!!this.impersonateToken);
  }

  impersonateUser(token?: string) {
    this.impersonateToken = token;
    if (token) {
      this.setCurrentUser(token);
    } else {
      this.setCurrentUser(this.getToken()!);
    }
  }

  getToken() {
    return this.impersonateToken || this.token;
  }

  saveToken(token: string) {
    this.storageService.set('authToken', token);

    this.token = token;
  }

  setCurrentUser(token: string | null) {
    try {
      if (!token || this.jwt.isTokenExpired(token)) {
        return;
      }
    }
    catch {
      this.saveToken("");
      return;
    }

    const decoded = this.jwt.decodeToken(token);
    if (decoded == null) {
      return;
    }


    try {
      const jData = JSON.parse(decoded.user) as CurrentUser;

      var isNewUser = this.currentUserID != jData.userID;

      this.currentUser.next(jData as CurrentUser);

      this.logger.log("User set to ID " + jData.userID + " - " + jData.name);

      if (isNewUser) {
        this.sendWSAuth();
      }

      if (this.redirectUrl !== null) {
        this.router.navigate([this.redirectUrl]);
        this.redirectUrl = null;
      }
    } catch (error) {

    }

  }

  signOut() {
    this.logger.info("Signing out");

    var promise = new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        this.storageService.remove('authToken');

        this.token = null;
        this.currentUser.next(null);

        if (this._refreshTokenSubscription) {
          this._refreshTokenSubscription.unsubscribe();
          this._refreshTokenSubscription = undefined;
        }

        if (this.socialUser) {
          this.socialAuth.signOut().then(resolve);
        } else {
          resolve();
        }
      }, 300);

    });

    return promise;
  }


  refreshToken() {
    if (!this.token || this.isImperosnating) {
      return;
    }

    if (!environment.production) {
      this.logger.log("Refresh user - " + this.currentUser.value?.name);
    }

    const apiAddress = environment.apiBase + 'user/RefreshToken';

    // Novo osvežavanje zamenjuje prethodno, i dok ono čeka ponovni pokušaj.
    this._refreshTokenSubscription?.unsubscribe();

    // Sledeće redovno osvežavanje je tek za 6 sati, pa se neuspeo zahtev (čvor se baš gasi, mreža
    // trepne) ponavlja dok ne uspe. Server izdaje nov token za istu sesiju i stari ne poništava,
    // pa ponavljanje ne škodi ni kad je prethodni odgovor izgubljen u putu.
    this._refreshTokenSubscription = this.http.get<GenericItemApiResponse<string>>(apiAddress).
      pipe(
        retryTransient(logRetry(this.logger, 'Token refresh')),
        takeUntil(
          this.currentUser.pipe(skip(1))
        ))
      .subscribe(
        result => {
          if (result.success) {
            this.saveToken(result.item);

            //            this.loginCompleted(result.item);

          }
        }
      );
  }

  /**
   * Registracija novog naloga — POST `user/SignUp` otvara nalog i šalje kod na eMail (CodeSent); prijavu završava
   * verifySignIn, posle čega korisnik nastavlja na dodavanje firme.
   * Greške vraćamo kao ishod (ne kao HTTP throw) — zato SkipApiResponseInterceptor,
   * da poruku prikaže forma, a ne globalni interceptor.
   */
  register(data: IRegisterData): Observable<RegisterResult> {
    const apiAddress = environment.apiBase + 'user/SignUp';

    const body = {
      firstName: data.firstName,
      lastName: data.lastName,
      eMail: data.eMail,
      password: data.password,
      phone: data.phone,
      recaptchaToken: data.recaptchaToken ?? ''
    };

    return this.http.post<GenericItemApiResponse<string>>(apiAddress, body, {
      headers: { 'SkipApiResponseInterceptor': '' }
    }).pipe(
      map(response => {
        if (response.success) {
          return RegisterResult.CodeSent;
        }

        if (response.errorCode === ErrorCodes.MailAlreadyRegistered) {
          return RegisterResult.MailAlreadyRegistered;
        }

        if (response.errorCode === ErrorCodes.CaptchaFailed) {
          return RegisterResult.CaptchaFailed;
        }

        if (response.errorCode === ErrorCodes.TooManyRequests) {
          return RegisterResult.TooManyRequests;
        }

        if (response.errorCode === ErrorCodes.InvalidPassword
          || response.errorCode === ErrorCodes.InvalidOperation) {
          return RegisterResult.InvalidData;
        }

        this.logger.warn('register failed', response.errorCode, response.errorDescription);

        return RegisterResult.Failed;
      })
    );
  }

  googleSignInExternal(googleTokenId: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + 'user/googlesigninexternal';

    return this.http.get<GenericItemApiResponse<string>>(apiAddress, {
      params: new HttpParams().set('googleTokenId', googleTokenId).set('emailCode', true),
      headers: { 'SkipApiResponseInterceptor': '' }
    })
      .pipe(
        map((result) => {
          if (result.success) {
            this.loginCompleted(result.item);

            return LoginResult.OK;
          }
          if (result.errorCode === ErrorCodes.EmailVerificationRequired) {
            // provajder nije potvrdio eMail naloga: kod je poslat na njega (jednom po nalogu)
            this.providerCodeEmail = result.details?.email || '';
            return LoginResult.EmailCodeSent;
          }
          if (result.errorCode === ErrorCodes.InvalidUserNamePassword) {
            return LoginResult.InvalidUserName;
          }

          return LoginResult.Disabled;
        })
      );

  }

  msSignInExternal(token: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + 'user/mssigninexternal';

    return this.http.get<GenericItemApiResponse<string>>(apiAddress, {
      params: new HttpParams().set('token', token).set('emailCode', true),
      headers: { 'SkipApiResponseInterceptor': '' }
    })
      .pipe(
        map((result) => {
          if (result.success) {
            this.loginCompleted(result.item);
            return LoginResult.OK;
          }
          if (result.errorCode === ErrorCodes.EmailVerificationRequired) {
            // provajder nije potvrdio eMail naloga: kod je poslat na njega (jednom po nalogu)
            this.providerCodeEmail = result.details?.email || '';
            return LoginResult.EmailCodeSent;
          }
          if (result.errorCode === ErrorCodes.InvalidUserNamePassword) {
            return LoginResult.InvalidUserName;
          }

          return LoginResult.Disabled;
        })
      );
  }

  facebookSignInExternal(fbTokenId: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + 'user/facebooksigninexternal';

    return this.http.get<GenericItemApiResponse<string>>(apiAddress, {
      params: new HttpParams().set('fbTokenId', fbTokenId).set('emailCode', true),
      headers: { 'SkipApiResponseInterceptor': '' }
    })
      .pipe(
        map((result) => {
          if (result.success) {
            this.loginCompleted(result.item);

            return LoginResult.OK;
          }
          if (result.errorCode === ErrorCodes.EmailVerificationRequired) {
            // provajder nije potvrdio eMail naloga: kod je poslat na njega (jednom po nalogu)
            this.providerCodeEmail = result.details?.email || '';
            return LoginResult.EmailCodeSent;
          }
          if (result.errorCode === ErrorCodes.InvalidUserNamePassword) {
            return LoginResult.InvalidUserName;
          }

          return LoginResult.Disabled;

        })
      );

  }


  /**
   * Prijava eMail-om (ili korisničkim imenom) i lozinkom — `user/SignIn`. Ispravan eMail koji nalog nikad nije potvrdio
   * dobija kod (EmailCodeSent), a prijavu završava verifySignIn; ponovni poziv je „pošalji kod ponovo“.
   */
  login(data: { username: string, password: string }): Observable<LoginResult> {
    const apiAddress = environment.apiBase + `user/SignIn`;

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, { eMail: data.username, password: data.password }, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result);
  }

  /** Kod iz registracije: nalog se tek sada otvara (eMail potvrđen) i korisnik je prijavljen. */
  confirmSignUp(eMail: string, code: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + `user/ConfirmSignUp`;

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, { eMail, code }, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result);
  }

  /** Kod iz eMail-a posle prijave (lozinkom ili preko provajdera): eMail postaje potvrđen, korisnik je prijavljen. */
  verifySignIn(eMail: string, code: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + `user/VerifySignIn`;

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, { eMail, code }, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result);
  }

  /** Kod ne stiže (pogrešno upisan eMail): uz tačnu lozinku kod ide na novu adresu → EmailCodeSent. */
  changeSignInEmail(eMail: string, password: string, newEMail: string): Observable<LoginResult> {
    const apiAddress = environment.apiBase + `user/ChangeSignInEmail`;

    return this.http.post<GenericItemApiResponse<string>>(apiAddress, { eMail, password, newEMail }, {
      headers: { 'SkipApiResponseInterceptor': '' }
    }).pipe(
      map(response => {
        if (response.success) {
          return LoginResult.EmailCodeSent;
        }

        switch (response.errorCode) {
          case ErrorCodes.MailAlreadyRegistered:
            return LoginResult.EmailTaken;
          case ErrorCodes.InvalidOperation:
            return LoginResult.InvalidEmail;
          case ErrorCodes.TooManyRequests:
            return LoginResult.TooManyRequests;
          case ErrorCodes.InvalidUserNamePassword:
            return LoginResult.InvalidUserName;
          default:
            return LoginResult.Disabled;
        }
      })
    );
  }

  loginWithToken(token: string) {
    const apiAddress = environment.apiBase + `user/loginWithToken`;
    var data = new FormData();
    data.append("token", token);

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, data, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result);
  }


  loginWithAPIKey(api: string) {
    const apiAddress = environment.apiBase + `user/loginWithAPI`;
    var data = new FormData();
    data.append("api", api);

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, data, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result);
  }

  /** Adresa na koju je server poslao kod kad je prijava stala na potvrdi eMail-a (posle prijave preko provajdera). */
  providerCodeEmail = '';

  private executeLoginRequest(request: Observable<GenericItemApiResponse<string>>, pinLogin = false) {
    var result = request.pipe(
      map(response => {
        if (response.success) {
          this.loginCompleted(response.item);

          return LoginResult.OK;
        }

        // Prijava PIN-om ima svoja značenja kodova (eMeni, LocalServer, POS i kasa server isto):
        //  - pogrešan PIN = InvalidUserNamePassword; stari eMeni vraća InvalidToken (prazan token
        //    klijent nikad ne šalje), a prazan PIN = InvalidPassword
        //  - AccessDenied BEZ poruke ("AccessDenied") = PinToken sačuvan na uređaju više ne važi.
        //    Stari eMeni vraća AccessDenied i za admin nalog, ali sa porukom ("User is admin...") —
        //    tada NE govorimo da je prijava promenjena (poruka traži odjavu uređaja), nego opšte.
        if (pinLogin) {
          switch (response.errorCode) {
            case ErrorCodes.InvalidUserNamePassword:
            case ErrorCodes.InvalidPassword:
            case ErrorCodes.InvalidToken:
              return LoginResult.InvalidPin;
            case ErrorCodes.AccessDenied:
              return response.errorDescription === 'AccessDenied' ? LoginResult.PinDeviceExpired : LoginResult.AccessDenied;
          }
        }

        if (response.errorCode === ErrorCodes.InvalidUserNamePassword) {
          return LoginResult.InvalidUserName;
        }
        else if (response.errorCode === ErrorCodes.InvalidToken) {
          return LoginResult.InvalidToken;
        }
        else if (response.errorCode === ErrorCodes.AccessDenied) {
          return LoginResult.AccessDenied;
        }
        else if (response.errorCode === ErrorCodes.PersonalCredentialRequired) {
          return LoginResult.PersonalCredentialRequired;
        }
        else if (response.errorCode === ErrorCodes.EmailVerificationRequired) {
          this.providerCodeEmail = response.details?.email || '';
          return LoginResult.EmailCodeSent;
        }
        else if (response.errorCode === ErrorCodes.AccountWithoutPassword) {
          return LoginResult.NoPassword;
        }
        else if (response.errorCode === ErrorCodes.TooManyRequests) {
          return LoginResult.TooManyRequests;
        }
        else if (response.errorCode === ErrorCodes.InvalidCode) {
          return LoginResult.InvalidCode;
        }
        else if (response.errorCode === ErrorCodes.MailAlreadyRegistered) {
          // registracija: adresu je zauzeo drugi nalog dok je kod čekao
          return LoginResult.EmailTaken;
        }

        return LoginResult.Disabled;
      })
    );

    return result;
  }


  sendWSAuth() {
    if (!this.token) {
      return;
    }

    const data: WebsocketData = {
      type: "auth",
      data: {
        "type": "token",
        "token": this.token,
      }
    };

    this.ws.send(data);
  }

  loginCompleted(token: string) {
    this.saveToken(token);
    this.setCurrentUser(token);

  }

  loginWithPin(companyID: number, pin: string, token: string) {

    const apiAddress = environment.apiBase + `user/loginWithPIN`;

    var data = {
      companyID: companyID,
      pin: pin,
      token: token
    };

    const result = this.http.post<GenericItemApiResponse<string>>(apiAddress, data, {
      headers: { 'SkipApiResponseInterceptor': '' }
    });

    return this.executeLoginRequest(result, true);
  }


}
